import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { checkinsAPI } from '../lib/api';
import { getRatingLabel, getRatingColor } from '../lib/utils';
import { toast } from 'sonner';

const ratingDescriptions = {
  mood: ['Very low', 'Low', 'Neutral', 'Good', 'Excellent'],
  energy: ['Exhausted', 'Tired', 'Moderate', 'Energetic', 'Very energetic'],
  sleep: ['Very poor', 'Poor', 'Fair', 'Good', 'Excellent'],
  digestion: ['Very poor', 'Poor', 'Fair', 'Good', 'Excellent'],
  pain: ['Severe', 'Moderate', 'Mild', 'Minimal', 'None'],
};

export default function CheckInPage() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [questionsRes, todayRes] = await Promise.all([
        checkinsAPI.getQuestions(),
        checkinsAPI.getToday()
      ]);
      
      setQuestions(questionsRes.data.questions || []);
      
      if (todayRes.data.completed) {
        setAlreadyCompleted(true);
        // Pre-fill with today's responses
        const checkin = todayRes.data.checkin;
        const existingResponses = {};
        checkin?.responses?.forEach(r => {
          existingResponses[r.question_id] = r.rating;
        });
        setResponses(existingResponses);
      }
    } catch (error) {
      console.error('Failed to load check-in data:', error);
      toast.error('Failed to load check-in questions');
    } finally {
      setLoading(false);
    }
  };

  const currentQuestion = questions[currentIndex];
  const isLastQuestion = currentIndex === questions.length - 1;
  const allAnswered = questions.every(q => responses[q.question_id] !== undefined);

  const handleRating = (rating) => {
    setResponses(prev => ({
      ...prev,
      [currentQuestion.question_id]: rating
    }));
    
    // Auto advance after short delay
    if (!isLastQuestion) {
      setTimeout(() => {
        setCurrentIndex(prev => prev + 1);
      }, 300);
    }
  };

  const handleSubmit = async () => {
    if (!allAnswered) {
      toast.error('Please answer all questions');
      return;
    }

    setSubmitting(true);
    try {
      const data = {
        responses: questions.map(q => ({
          question_id: q.question_id,
          question_text: q.question_text,
          category: q.category,
          rating: responses[q.question_id]
        })),
        notes: notes || null
      };

      await checkinsAPI.submit(data);
      toast.success('Check-in completed!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to submit check-in');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  if (alreadyCompleted) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto" data-testid="checkin-completed">
          <div className="soft-card p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-[#8DA399]/20 flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-[#8DA399]" />
            </div>
            <h1 className="font-heading text-3xl text-[#4A2C2A] mb-3">
              Already Checked In Today!
            </h1>
            <p className="text-[#4A2C2A]/70 mb-6">
              You've already completed your daily check-in. Come back tomorrow to track your progress.
            </p>
            
            <div className="bg-[#FDFBF7] rounded-xl p-6 mb-6">
              <h3 className="font-medium text-[#4A2C2A] mb-4">Today's Ratings</h3>
              <div className="grid grid-cols-5 gap-3">
                {questions.map(q => (
                  <div key={q.question_id} className="text-center">
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2"
                      style={{ backgroundColor: getRatingColor(responses[q.question_id]) + '30' }}
                    >
                      <span className="font-medium" style={{ color: getRatingColor(responses[q.question_id]) }}>
                        {responses[q.question_id]}
                      </span>
                    </div>
                    <span className="text-xs text-[#4A2C2A]/60 capitalize">{q.category}</span>
                  </div>
                ))}
              </div>
            </div>

            <Button 
              onClick={() => navigate('/dashboard')}
              className="bg-[#4A2C2A] text-white hover:bg-[#382120] rounded-full px-8"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto" data-testid="checkin-page">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-[#4A2C2A]/60">
              Question {currentIndex + 1} of {questions.length}
            </span>
            <span className="text-sm text-[#4A2C2A]/60">
              {Math.round(((currentIndex + 1) / questions.length) * 100)}%
            </span>
          </div>
          <div className="h-2 bg-[#E8A095]/20 rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#E8A095] rounded-full transition-all duration-300"
              style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Question Card */}
        <div className="checkin-card mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[#E8A095]/20 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-[#E8A095]" />
            </div>
            <span className="text-sm font-medium text-[#E8A095] uppercase tracking-wider">
              {currentQuestion?.category}
            </span>
          </div>

          <h2 className="font-heading text-2xl text-[#4A2C2A] mb-8">
            {currentQuestion?.question_text}
          </h2>

          {/* Rating Scale */}
          <div className="flex justify-between gap-2">
            {[1, 2, 3, 4, 5].map((rating) => {
              const isSelected = responses[currentQuestion?.question_id] === rating;
              const descriptions = ratingDescriptions[currentQuestion?.category] || [];
              
              return (
                <button
                  key={rating}
                  onClick={() => handleRating(rating)}
                  className={`flex-1 py-4 px-2 rounded-xl border-2 transition-all ${
                    isSelected
                      ? 'border-[#E8A095] bg-[#E8A095] text-white'
                      : 'border-[#E8A095]/20 hover:border-[#E8A095]/40 bg-white'
                  }`}
                  data-testid={`rating-${rating}`}
                >
                  <div className="text-2xl font-heading mb-1">{rating}</div>
                  <div className={`text-xs ${isSelected ? 'text-white/80' : 'text-[#4A2C2A]/50'}`}>
                    {descriptions[rating - 1]}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Notes (on last question) */}
        {isLastQuestion && (
          <div className="soft-card p-6 mb-6 animate-fade-up">
            <h3 className="font-medium text-[#4A2C2A] mb-3">Any additional notes?</h3>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="How are you feeling overall? Any specific symptoms or observations..."
              className="input-warm min-h-[100px]"
              data-testid="checkin-notes"
            />
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
            disabled={currentIndex === 0}
            className="text-[#4A2C2A]"
            data-testid="checkin-prev-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-2" /> Previous
          </Button>

          {isLastQuestion ? (
            <Button
              onClick={handleSubmit}
              disabled={!allAnswered || submitting}
              className="bg-[#8DA399] text-white hover:bg-[#7A9086] rounded-full px-8"
              data-testid="checkin-submit-btn"
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Submitting...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Check className="w-4 h-4" /> Complete Check-in
                </span>
              )}
            </Button>
          ) : (
            <Button
              onClick={() => setCurrentIndex(prev => prev + 1)}
              disabled={!responses[currentQuestion?.question_id]}
              className="bg-[#4A2C2A] text-white hover:bg-[#382120] rounded-full px-8"
              data-testid="checkin-next-btn"
            >
              Next <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>

        {/* Quick Jump */}
        <div className="flex items-center justify-center gap-2 mt-8">
          {questions.map((q, i) => (
            <button
              key={q.question_id}
              onClick={() => setCurrentIndex(i)}
              className={`w-3 h-3 rounded-full transition-all ${
                i === currentIndex
                  ? 'bg-[#E8A095] scale-125'
                  : responses[q.question_id]
                    ? 'bg-[#8DA399]'
                    : 'bg-[#E8A095]/30'
              }`}
              data-testid={`question-dot-${i}`}
            />
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
